import { doc, deleteDoc, updateDoc, addDoc, collection, setDoc, getDocs } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';

import { Addons, type JoinType } from '@labradorsports/constants';
import {
    createNestedUpdates,
    setCookie,
    getUserRole,
    createException,
    GeneralErrors,
    getCommonError,
    processTeam,
    processProgram,
    getContactMap,
} from '@labradorsports/utils';

import firestore from '../../firebase-config.js';
import { AffiliateCouponCookieName, DataTags } from '../../shared/constants.js';
import { trackUserProperties } from '../../shared/utils.js';
import { createApiSlice } from '../api.js';
import { BillingSettings, ProgramSettings } from '../billing/index.js';
import { mainActions } from '../main/index.js';
import { routerActions } from '../router/index.js';
import { RouterSelectors, Selectors } from '../state.js';
import { teamsActions } from '../teams/index.js';
import { CustomMutationDefinition, CustomQueryDefinition, QueryApi } from '../types.js';

const joinTeamSuccess = ({ data: result }, { dispatch, extra }: QueryApi) => {
    if (!result) {
        return;
    }

    const { logger } = extra;

    if (result.joinedTeams) {
        const teamId = Object.keys(result.joinedTeams)[0];

        dispatch(routerActions.SetActiveTeam(teamId));
    }

    logger.log('joinTeam complete', { joinedAs: result.joinedAs, result });

    return result;
};

function fixCreatedDate(entity) {
    return {
        ...entity,
        createdDate: new Date(entity.createdDate),
    };
}

export const teamsApi = createApiSlice({
    fetchTeams: {
        type: 'query',
        extraOptions: {
            showLoading: true,
        },
        query: () => ({
            path: 'teams/retrieveTeams',
        }),
        transformResponse: (result) => {
            const { owned = [], joined = [], programs = [], pendingJoin = [] } = result;

            return {
                ownedTeams: owned.map(fixCreatedDate).map(processTeam),
                joinedTeams: joined.map(fixCreatedDate).map(processTeam),
                programs: programs
                    .map(fixCreatedDate)
                    .map(processProgram)
                    .map((program) => ({
                        ...program,
                        teams: owned.filter((team: any) => team.programId === program.id),
                    })),
                pendingJoinTeams: pendingJoin.map((team) => ({
                    ...team,
                    pending: true,
                })),
            };
        },
        onQueryEnded: (
            { data: { ownedTeams = [], joinedTeams = [], programs = [], pendingJoinTeams = [] } = {} },
            { dispatch, extra, getState }
        ) => {
            const state = getState();
            const { logger } = extra;

            logger.log('fetchTeams complete', {
                programs: programs.length,
                ownedTeams: ownedTeams.length,
                joinedTeams: joinedTeams.length,
                pendingJoinTeams: pendingJoinTeams.length,
            });

            const uid = Selectors.uid(state);

            trackUserProperties(uid, {
                ownedPrograms: programs.length,
                ownedTeams: ownedTeams.length,
                role: getUserRole(uid, programs, joinedTeams) ?? 'NONE',
            });

            programs.map((program: any) =>
                dispatch(teamsApi.endpoints.loadEntityLogo.initiate({ programId: program.id }))
            );
        },
        providesTags: ({ ownedTeams = [], joinedTeams = [], programs = [], pendingJoinTeams = [] } = {}) => {
            const listTags = [
                { type: DataTags.OWNED_TEAM, id: 'LIST' },
                { type: DataTags.JOINED_TEAM, id: 'LIST' },
                { type: DataTags.OWNED_PROGRAM, id: 'LIST' },
                { type: DataTags.PENDING_TEAM, id: 'LIST' },
            ];

            const ownedTags = ownedTeams.map((team) => ({ type: DataTags.OWNED_TEAM, id: team.id }));
            const joinedTags = joinedTeams.map((team) => ({ type: DataTags.JOINED_TEAM, id: team.id }));
            const programTags = programs.map((program) => ({ type: DataTags.OWNED_PROGRAM, id: program.id }));
            const pendingTags = pendingJoinTeams.map((team) => ({ type: DataTags.PENDING_TEAM, id: team.id }));

            return listTags.concat(ownedTags).concat(joinedTags).concat(programTags).concat(pendingTags);
        },
    } as CustomQueryDefinition<void, any>,

    deleteProgram: {
        type: 'mutation',
        queryFn: async ({ programId }) => {
            // Delete the program - cloud functions will delete related data automatically
            await deleteDoc(doc(firestore, 'programs', programId));
        },
        invalidatesTags: (result, meta, { programId }) => [{ type: DataTags.OWNED_PROGRAM, id: programId }],
    } as CustomMutationDefinition<{ programId: string }, any>,

    createProgramSubscription: {
        type: 'mutation',
        query: ({ creatingProgram, creatingBilling }) => {
            const { logoFile, teams, ...teamConfig } = creatingProgram;

            const { couponProperties, ...billingConfig } = creatingBilling;

            return {
                path: 'teams/createProgramSubscription',
                body: {
                    teamSettings: {
                        teams: teams?.filter((team) => team.name !== '' && team.sport !== ''),
                        ...teamConfig,
                        ...billingConfig,
                    },
                },
            };
        },
        onQueryEnded: ({ data: { entity, billingEntity } }, { extra }) => {
            const { logger } = extra;

            logger.log('createProgramSubscription complete', {
                entityId: entity.id,
                cardId: billingEntity.cardId,
            });

            // Clear affiliate cookie as the coupon should only show up once
            setCookie(AffiliateCouponCookieName, '', new Date(0));
        },
        invalidatesTags: () => [
            { type: DataTags.BILLING_ENTITY, id: 'LIST' },
            { type: DataTags.OWNED_TEAM, id: 'LIST' },
            { type: DataTags.OWNED_PROGRAM, id: 'LIST' },
        ],
    } as CustomMutationDefinition<{ creatingProgram: ProgramSettings; creatingBilling: BillingSettings }, any>,

    updateProgramTeams: {
        type: 'mutation',
        query: ({ programId, oldTeams, newTeams, programAddons }) => {
            const [added, modified] = newTeams.reduce(
                ([addedAcc, modifiedAcc], newTeam) => {
                    if (!newTeam.id) {
                        return [[...addedAcc, newTeam], modifiedAcc];
                    }

                    const existing = oldTeams.find((tm) => tm.id === newTeam.id);

                    if (
                        existing.name !== newTeam.name ||
                        existing.sport !== newTeam.sport ||
                        existing.collegiateTeam !== newTeam.collegiateTeam ||
                        existing.addons?.[Addons.PLAYERS] !== newTeam.addons?.[Addons.PLAYERS] ||
                        existing.addons?.[Addons.COACHES] !== newTeam.addons?.[Addons.COACHES]
                    ) {
                        return [addedAcc, [...modifiedAcc, { ...newTeam }]];
                    }

                    return [addedAcc, modifiedAcc];
                },
                [[], []]
            );

            const removed = oldTeams.filter((oldTeam) => !newTeams.find((tm) => tm.id === oldTeam.id));

            return {
                path: 'teams/updateProgramTeams',
                body: {
                    programId,
                    added,
                    modified,
                    removed,
                    addons: programAddons,
                    quantity: newTeams.length,
                },
            };
        },
        invalidatesTags: (results, meta, { programId, oldTeams }) => [
            { type: DataTags.BILLING_ENTITY, id: programId },
            { type: DataTags.OWNED_PROGRAM, id: programId },
            { type: DataTags.OWNED_TEAM, id: 'LIST' },
            ...oldTeams.map(({ id }) => ({ type: DataTags.OWNED_TEAM, id })),
        ],
    } as CustomMutationDefinition<{ programId: string; oldTeams: any[]; newTeams: any[]; programAddons: any }, any>,

    loadEntityLogo: {
        type: 'query',
        queryFn: async ({ programId }, { extra }) => {
            if (SSR) {
                return {
                    data: null,
                };
            }

            const { logger } = extra;

            try {
                const url = await getDownloadURL(ref(getStorage(), `teams/${programId}/logo.png`));

                return {
                    data: url,
                };
            } catch (error) {
                if (error.code !== 'storage/object-not-found') {
                    logger.exception(getCommonError(error));
                }
            }

            return {
                data: null,
            };
        },
        providesTags: (result, meta, { programId }) => [{ type: DataTags.ENTITY_LOGO, id: programId }],
    } as CustomQueryDefinition<{ programId: string }, string | null>,

    updateEntityLogo: {
        type: 'mutation',
        query: ({ entityId, logo }) => ({
            path: 'teams/updateEntityLogo',
            body: {
                entityId,
                logo,
            },
        }),
        invalidatesTags: ({ entityId }) => [{ type: DataTags.ENTITY_LOGO, id: entityId }],
    } as CustomMutationDefinition<{ entityId: string; logo: any }, any>,

    updateProgram: {
        type: 'mutation',
        extraOptions: {
            unloggableArg: ['updates'],
        },
        queryFn: async ({ entityId, updates }, { dispatch, getState }) => {
            const state = getState();
            const selector = teamsApi.endpoints.fetchTeams.select();
            const {
                data: { programs },
            } = selector(state) as any;

            const { teams, addons, ...entityUpdates } = updates;

            const existing = programs.find((program) => program.id === entityId);

            if (teams || addons) {
                await dispatch(
                    teamsApi.endpoints.updateProgramTeams.initiate({
                        programId: entityId,
                        oldTeams: existing.teams,
                        newTeams: teams ?? existing.teams,
                        programAddons: addons,
                    })
                );
            }

            await updateDoc(doc(firestore, 'programs', entityId), entityUpdates);
        },
        invalidatesTags: (results, meta, { entityId }) => [{ type: DataTags.OWNED_PROGRAM, id: entityId }],
    } as CustomMutationDefinition<{ entityId: string; updates: any }, any>,

    updateProgramBranding: {
        type: 'mutation',
        queryFn: async ({ entityId, updates }, { dispatch }) => {
            const { logo, ...entityUpdates } = updates;
            await updateDoc(doc(firestore, 'programs', entityId), entityUpdates);

            await dispatch(
                teamsApi.endpoints.updateEntityLogo.initiate({
                    entityId,
                    logo,
                })
            );
        },
        invalidatesTags: (results, meta, { entityId }) => [{ type: DataTags.OWNED_PROGRAM, id: entityId }],
    } as CustomMutationDefinition<{ entityId: string; updates: any }, any>,

    getTeamMembers: {
        type: 'query',
        query: ({ teamIds, programIds }) => ({
            path: 'teams/getTeamMembers',
            query: {
                teams: teamIds,
                programs: programIds,
            },
        }),
        providesTags: (result, error, { teamIds = [], programIds = [] }) => {
            const teamTags = teamIds.map((id) => ({ type: DataTags.TEAM_MEMBER, id: `${id}_LIST` }));
            const programTags = programIds.map((id) => ({ type: DataTags.TEAM_MEMBER, id: `${id}_LIST` }));

            if (!result) {
                return teamTags.concat(programTags);
            }

            const memberTags = Object.keys(result).map((k) => ({ type: DataTags.TEAM_MEMBER, id: k }));

            return memberTags.concat(teamTags).concat(programTags);
        },
    } as CustomQueryDefinition<{ teamIds: string[]; programIds: string[] }, any>,

    joinTeam: {
        type: 'mutation',
        extraOptions: {
            suppressError: true,
        },
        query: ({ teamKey }) => ({
            path: 'teams/joinTeam',
            body: { teamKey },
        }),
        onQueryEnded: joinTeamSuccess,
        invalidatesTags: () => [{ type: DataTags.JOINED_TEAM, id: 'LIST' }],
    } as CustomMutationDefinition<{ teamKey: string }, any>,

    joinTeamLink: {
        type: 'mutation',
        isValid: ({ entityId, linkCode }) => Boolean(entityId && linkCode),
        query: ({ entityId, linkCode }) => ({
            path: 'teams/joinTeamLink',
            body: {
                entityId,
                linkCode,
            },
        }),
        onQueryEnded: joinTeamSuccess,
        invalidatesTags: () => [{ type: DataTags.JOINED_TEAM, id: 'LIST' }],
    } as CustomMutationDefinition<{ entityId: string; linkCode: string }, any>,

    removeTeamMembers: {
        type: 'mutation',
        query: ({ entityId, memberIds }) => ({
            path: 'teams/removeTeamMembers',
            body: {
                entityId,
                memberIds,
            },
        }),
        invalidatesTags: (result, meta, { entityId, memberIds }) => {
            const entityTags: any[] = [
                { type: DataTags.OWNED_TEAM, id: entityId },
                { type: DataTags.OWNED_PROGRAM, id: entityId },
            ];

            return entityTags.concat(memberIds.map((k) => ({ type: DataTags.TEAM_MEMBER, id: k })));
        },
    } as CustomMutationDefinition<{ entityId: string; memberIds: string[] }, any>,

    updatePlayers: {
        type: 'mutation',
        queryFn: async ({ teamId, playerOrPlayers }, { extra }) => {
            const { logger } = extra;
            const players = Array.isArray(playerOrPlayers) ? playerOrPlayers : [playerOrPlayers];

            const updatedPlayers = await players.reduce(async (accumulator, { contacts, ...player }) => {
                const updated = await accumulator;

                if (player.id) {
                    logger.log('updatePlayer existing', { teamId, id: player.id });
                    const { id, ...update } = player;

                    await updateDoc(doc(firestore, 'teams', teamId, 'roster', id), update);

                    logger.log('updatePlayer existing complete');

                    return [...updated, player];
                }

                logger.log('updatePlayer new', { teamId });
                const snapshot = await addDoc(collection(firestore, 'teams', teamId, 'roster'), player);

                logger.log('updatePlayer new complete', { id: snapshot.id });

                const newPlayer = {
                    ...player,
                    id: snapshot.id,
                };

                if (contacts?.length > 0) {
                    logger.log('adding contacts', { contacts: contacts.length });

                    await contacts.reduce(async (acc: any, contact: any) => {
                        await acc;

                        const fullContact = {
                            ...contact,
                            playerId: newPlayer.id,
                        };

                        const added = await addDoc(collection(firestore, 'teams', teamId, 'roster'), fullContact);

                        logger.log('added contact', { id: added.id });
                    }, Promise.resolve());
                }

                return [...updated, newPlayer];
            }, Promise.resolve([]));

            return {
                data: updatedPlayers,
            };
        },
        invalidatesTags: (result, meta, { teamId }) => [{ type: DataTags.ROSTER_PERSON, id: `${teamId}_LIST` }],
    } as CustomMutationDefinition<{ teamId: string; playerOrPlayers: any | any[] }, any>,

    saveDepthChart: {
        type: 'mutation',
        extraOptions: {
            unloggableArg: ['depth'],
        },
        queryFn: async ({ activeTeam, depth = {} }) => {
            await setDoc(doc(firestore, 'teams', activeTeam, 'roster', 'depthChart'), depth);
        },
        onQueryEnded: (result, { dispatch }) => {
            dispatch(teamsActions.DepthChartModified(false));
        },
        invalidatesTags: (result, meta, { activeTeam }) => [{ type: DataTags.DEPTH_CHART, id: activeTeam }],
    } as CustomMutationDefinition<{ activeTeam: string; depth?: any }, any>,

    removePlayers: {
        type: 'mutation',
        query: ({ activeTeam, playerIds }) => ({
            path: 'teams/removeTeamPlayers',
            body: {
                teamId: activeTeam,
                playerIds,
            },
        }),
        invalidatesTags: (result, meta, { activeTeam }) => [{ type: DataTags.DEPTH_CHART, id: activeTeam }],
    } as CustomMutationDefinition<{ activeTeam: string; playerIds: string[] }, any>,

    retrieveRoster: {
        type: 'query',
        isValid: ({ teamId }) => Boolean(teamId),
        queryFn: async ({ teamId }, { extra }) => {
            const { logger } = extra;

            const snapshot = await getDocs(collection(firestore, 'teams', teamId, 'roster'));

            const roster: any[] = [];
            let depthChart;
            snapshot.docs.forEach((rosterDoc) => {
                if (rosterDoc.id === 'depthChart') {
                    depthChart = rosterDoc.data();
                    return;
                }

                const player = rosterDoc.data();

                if (player) {
                    player.id = rosterDoc.id;
                    player.joinedAt = player.joinedAt ? player.joinedAt.toDate() : null;
                    roster.push(player);
                }
            });

            logger.log('retrieveRoster complete', { size: roster.length });

            const playerRoster = roster.filter(
                (person: any) => !person.playerId || !roster.some(({ id }) => id === person.playerId)
            );

            const contactMap = getContactMap(roster, playerRoster);

            return {
                data: {
                    roster,
                    depthChart,
                    playerRoster,
                    contactMap,
                },
            };
        },
        onQueryEnded: ({ data: { roster } }, { dispatch, getState }) => {
            const state = getState();
            const rosterPlayerId = RouterSelectors.rosterPlayerId(state);

            if (!roster.find(({ id }) => id === rosterPlayerId)) {
                dispatch(routerActions.SetRosterPlayer(null));
            }

            dispatch(teamsActions.DepthChartModified(false));
        },
        providesTags: (result, error, { teamId }) => {
            const baseTags = [
                { type: DataTags.ROSTER_PERSON, id: `${teamId}_LIST` },
                { type: DataTags.DEPTH_CHART, id: teamId },
            ];

            if (!result) {
                return baseTags;
            }

            return result.roster.map((person) => ({ type: DataTags.ROSTER_PERSON, id: person.id })).concat(baseTags);
        },
    } as CustomQueryDefinition<{ teamId: string }, any>,

    inviteMembers: {
        type: 'mutation',
        query: ({ entityId, playerEmails = [], coachEmails = [], adminEmails = [] }) => ({
            path: 'teams/inviteMembers',
            body: {
                entityId,
                playerEmails,
                coachEmails,
                adminEmails,
            },
        }),
        onQueryEnded: ({ data: { failedEmails } }, { dispatch }) => {
            if (failedEmails.length > 0) {
                dispatch(mainActions.GenericAlert(`These invites failed to send: ${failedEmails.join(', ')}`));
            } else {
                dispatch(mainActions.GenericAlert('Invites sent successfully.'));
            }
        },
    } as CustomMutationDefinition<
        { entityId: string; playerEmails?: string[]; coachEmails?: string[]; adminEmails?: string[] },
        any
    >,

    emailTeam: {
        type: 'mutation',
        extraOptions: {
            unloggableArg: ['recipients', 'subject', 'body', 'roster'],
        },
        query: ({ recipients, subject, body, roster }) => {
            return {
                path: 'teams/emailTeam',
                body: {
                    recipients,
                    subject,
                    body,
                },
            };
        },
    } as CustomMutationDefinition<
        { recipients: { email: string; firstName: string }[]; subject: string; body: string; roster: any[] },
        any
    >,

    setTeamFlags: {
        type: 'mutation',
        queryFn: async ({ activeTeam, flags }) => {
            await updateDoc(
                doc(firestore, 'teams', activeTeam),
                createNestedUpdates({
                    flags,
                })
            );
        },
        invalidatesTags: (result, meta, { activeTeam }) => [{ type: DataTags.OWNED_TEAM, id: activeTeam }],
    } as CustomMutationDefinition<{ activeTeam: string; flags: any }, any>,

    setCoachPlaceholders: {
        type: 'mutation',
        extraOptions: {
            unloggableArg: ['coaches'],
        },
        queryFn: async ({ activeTeam, coaches }) => {
            await updateDoc(doc(firestore, 'teams', activeTeam), {
                coachPlaceholders: coaches,
            });
        },
        invalidatesTags: (result, meta, { activeTeam }) => [{ type: DataTags.OWNED_TEAM, id: activeTeam }],
    } as CustomMutationDefinition<{ activeTeam: string; coaches: any[] }, any>,

    getInviteLink: {
        type: 'query',
        extraOptions: {
            unloggableArg: ['email'],
        },
        query: ({ email, type, entityId }) => ({
            path: 'teams/inviteLink',
            query: {
                email,
                type,
                entityId,
            },
        }),
        transformResponse: ({ link }) => link,
    } as CustomQueryDefinition<{ email: string; type: string; entityId: string }, any>,

    getInvitePreview: {
        type: 'query',
        extraOptions: {
            unloggableArg: ['email'],
        },
        query: ({ type, entityId, email }) => ({
            path: 'teams/previewInvite',
            query: {
                email,
                type,
                entityId,
            },
        }),
        transformResponse: (result) => {
            return result.html;
        },
    } as CustomQueryDefinition<{ type: string; entityId: string; email?: string }, any>,

    pendingRoster: {
        type: 'query',
        isValid: ({ teamId }) => Boolean(teamId),
        queryFn: async ({ teamId }) => {
            const snapshot = await getDocs(collection(firestore, 'teams', teamId, 'pendingRoster'));

            const roster: any[] = snapshot.docs.map((rosterDoc) => {
                const player = rosterDoc.data();

                if (player) {
                    player.id = rosterDoc.id;
                    player.joinedAt = player.joinedAt ? player.joinedAt.toDate() : null;
                }

                return player;
            });

            return {
                data: roster,
            };
        },
        providesTags: (result, meta, { teamId }) => [{ type: DataTags.PENDING_ROSTER, id: `${teamId}_LIST` }],
    } as CustomQueryDefinition<{ teamId: string }, any>,

    updatePendingRoster: {
        type: 'mutation',
        query: ({ teamId, accept, reject, assign }) => ({
            path: 'teams/updatePendingMembers',
            body: {
                teamId,
                accept,
                reject,
                assign,
            },
        }),
        onQueryEnded: ({ data: result }) => {
            if (!result.success) {
                mainActions.GenericError(
                    createException(GeneralErrors.UNKNOWN_ERROR, { details: 'Some updates were not successful' })
                );
            }
        },
        invalidatesTags: (result, meta, { teamId }) => [
            { type: DataTags.PENDING_ROSTER, id: `${teamId}_LIST` },
            { type: DataTags.ROSTER_PERSON, id: `${teamId}_LIST` },
        ],
    } as CustomMutationDefinition<{ teamId: string; accept: string[]; reject: string[]; assign: any }, any>,

    changeUserRole: {
        type: 'mutation',
        query: ({ teamId, uid, id, newRole }) => ({
            path: 'teams/changeUserRole',
            body: {
                teamId,
                uid,
                playerId: id,
                newRole,
            },
        }),
        invalidatesTags: (result, meta, { teamId }) => [
            { type: DataTags.TEAM_MEMBER, id: `${teamId}_LIST` },
            { type: DataTags.ROSTER_PERSON, id: `${teamId}_LIST` },
            { type: DataTags.DEPTH_CHART, id: teamId },
        ],
    } as CustomMutationDefinition<{ teamId: string; uid: string; id: string; newRole: JoinType }, any>,

    leaveTeam: {
        type: 'mutation',
        query: ({ teamId }) => ({
            path: 'teams/leaveTeam',
            body: { teamId },
        }),
        invalidatesTags: (result, meta, { teamId }) => [{ type: DataTags.JOINED_TEAM, id: teamId }],
    } as CustomMutationDefinition<{ teamId: string }, any>,
});

export const {
    useChangeUserRoleMutation,
    useCreateProgramSubscriptionMutation,
    useDeleteProgramMutation,
    useEmailTeamMutation,
    useFetchTeamsQuery,
    useGetInviteLinkQuery,
    useGetInvitePreviewQuery,
    useGetTeamMembersQuery,
    useInviteMembersMutation,
    useJoinTeamLinkMutation,
    useJoinTeamMutation,
    useLoadEntityLogoQuery,
    usePendingRosterQuery,
    useRemovePlayersMutation,
    useRemoveTeamMembersMutation,
    useRetrieveRosterQuery,
    useSaveDepthChartMutation,
    useSetCoachPlaceholdersMutation,
    useSetTeamFlagsMutation,
    useUpdateEntityLogoMutation,
    useUpdatePlayersMutation,
    useUpdatePendingRosterMutation,
    useUpdateProgramMutation,
    useUpdateProgramTeamsMutation,
    useUpdateProgramBrandingMutation,
    useLeaveTeamMutation,
} = teamsApi;
